export { DiceRoller, } from "./DiceRoller";
export { RerollCard, } from "./RerollCard";
export { RerollModal, } from "./RerollModal";
export { getRollHistoryCount, retrieveRollHistory, storeRollHistory, } from "./RollStorage";
export { Results, renderPoolResultCard, renderPoolResultText, } from "./Results";
export { HistoryModal as RollLog, } from "./HistoryModal";
export * as Icons from "./Icons";

// rolls
export enum RollType {
    Pool = "pool",
    Rouse = "rouse",
    D10 = "d10",
};

export type PoolRoll = {
    type: RollType.Pool;
    pool: number; // 0 - 5
    hunger?: number;
    difficulty?: number;
};

export type PoolRollResult = {
    type: RollType.Pool;
    pool: number[];
    hunger?: number[];
    difficulty?: number;
    config?: PoolRoll;
    timestamp: number;
    reroll?: boolean;
};

export type SingleRoll = {
    type: RollType.D10 | RollType.Rouse;
};

export type SingleRollResult = {
    type: RollType;
    roll: number;
    timestamp: number;
};

export type PoolRollOutcome = {
    difficulty?: number;

    // pool
    poolSuccesses: number;
    poolCrits: number;

    // hunger
    hungerFailures?: number;
    hungerSuccesses?: number;
    hungerCrits?: number;

    // overall
    successes: number;
    crits: number;
};

// dice rolls
export const rollSingle = (config: SingleRoll): SingleRollResult => {
    const { type, } = config;

    // not too concerned with the specific milliseconds
    const timestamp = Date.now();

    switch (type) {
        case RollType.Rouse:
            return {
                type: RollType.Rouse,
                roll: rollD10(),
                timestamp,
            };

        case RollType.D10:
            return {
                type: RollType.D10,
                roll: rollD10(),
                timestamp,
            };
    }
};

export const rollPool = (config: PoolRoll): PoolRollResult => {
    const { pool, hunger, difficulty, } = config;

    // if hunger is greater than pool, all the dice are hunger dice
    // but we never roll more hunger dice than our pool
    // calculate the pool
    const poolDice = hunger !== undefined ?
        pool < hunger ? 0 : Math.max(pool - hunger, 0) :
        pool;
    const hungerDice = !poolDice ? pool : hunger;

    // roll pool dice & sort in descending order
    const poolResults = [...Array(poolDice)].map(value => rollD10()).sort((a, b) => b - a);
    const hungerResults = hunger !== undefined ?
        [...Array(hungerDice)].map(value => rollD10()).sort((a, b) => b - a) :
        undefined;

    return {
        type: RollType.Pool,
        pool: poolResults,
        hunger: hungerResults,
        difficulty,
        timestamp: Date.now(),
    };
};

// dice roll interpreting
export const getPoolOutcome = (result: PoolRollResult): PoolRollOutcome => {
    const { pool, hunger, difficulty, } = result;

    const poolSuccesses = pool.filter(n => (n >= 6 && n < 10));
    const poolCrits = pool.filter(n => n === 10);

    const hungerSuccesses = hunger?.filter(n => (n >= 6 && n < 10));
    const hungerFailures = hunger?.filter(n => n === 1);
    const hungerCrits = hunger?.filter(n => n === 10);

    let crits = 0;
    let successes = poolSuccesses.length + (hungerSuccesses?.length || 0);

    if (poolCrits.length || hungerCrits?.length) {
        crits = poolCrits.length + (hungerCrits?.length || 0);
        successes += ((crits - (crits % 2)) * 2) + (crits % 2);
    }

    return {
        difficulty,

        poolSuccesses: poolSuccesses.length,
        poolCrits: poolCrits.length,

        hungerSuccesses: hungerSuccesses?.length,
        hungerFailures: hungerFailures?.length,
        hungerCrits: hungerCrits?.length,

        successes,
        crits,
    }
};

// TODO: use Random.org API?
export const rollD10 = (): number => Math.floor(Math.random() * 10) + 1;
